import React, { useMemo, useState } from "react";
import { Alert, Form, Modal, Spinner } from "react-bootstrap";
import AdminButton from "../../../../components/admin/AdminButton";
import CollectionSelector from "./CollectionSelector";
import type { CollectionsMap } from "./CollectionSelector";

type Props = {
  show: boolean;
  backup: any | null;
  isScheduling: boolean;
  onCancel: () => void;
  onRestore: (opts: { restoreAll: boolean; collections: string[] }) => void;
};

const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const formatDate = (dateString: string | null | undefined, fallback = "N/A") => {
  if (!dateString) return fallback;
  return new Date(dateString).toLocaleString();
};

export default function RestoreModal({ show, backup, isScheduling, onCancel, onRestore }: Props) {
  const [restoreAll, setRestoreAll] = useState(true);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [confirmationText, setConfirmationText] = useState("");

  const collections: CollectionsMap = useMemo(() => {
    const raw = backup?.collections;
    if (!raw) return {};
    // BackupJob.collections is a Map in Mongo; when serialized it generally becomes a plain object.
    // Keep it permissive here so the UI never crashes.
    if (typeof raw === "object" && !Array.isArray(raw)) return raw as CollectionsMap;
    return {};
  }, [backup]);

  const selectedCollectionNames = useMemo(() => {
    return Object.keys(selected).filter((k) => selected[k]);
  }, [selected]);

  const backupFileName = backup?.r2?.file_name || backup?.google_drive?.file_name || null;
  const sizeBytes =
    backup?.r2?.file_size_bytes || backup?.google_drive?.file_size_bytes || backup?.file_size || 0;

  const canSubmit = useMemo(() => {
    if (!backup) return false;
    if (!backupFileName) return false;
    if (confirmationText.trim().toUpperCase() !== "RESTORE") return false;
    if (!restoreAll && selectedCollectionNames.length === 0) return false;
    return true;
  }, [backup, backupFileName, confirmationText, restoreAll, selectedCollectionNames.length]);

  const handleClose = () => {
    if (isScheduling) return;
    // Reset state when closing so the next open starts safe.
    setRestoreAll(true);
    setSelected({});
    setConfirmationText("");
    onCancel();
  };

  const handleToggleCollection = (name: string) => {
    setSelected((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleRestore = () => {
    if (!canSubmit) return;
    onRestore({ restoreAll, collections: selectedCollectionNames });
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Restore Backup</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!backup ? (
          <Alert variant="info" className="mb-0">
            No backup selected.
          </Alert>
        ) : (
          <>
            <div className="mb-3">
              <div>
                <strong>Backup:</strong> {formatDate(backup.timestamp)}
              </div>
              <div>
                <strong>Environment:</strong>{" "}
                <span className="text-uppercase">{backup.environment || "N/A"}</span>
              </div>
              <div>
                <strong>Size:</strong> {formatBytes(sizeBytes)}
              </div>
              {backupFileName && (
                <div className="text-muted mt-1" style={{ wordBreak: "break-word" }}>
                  <small>{backupFileName}</small>
                </div>
              )}
            </div>

            <Alert variant="warning">
              WARNING: This will overwrite existing data using a safe swap restore. A pre-restore copy
              is kept for rollback.
            </Alert>

            <CollectionSelector
              collections={collections}
              restoreAll={restoreAll}
              selected={selected}
              onChangeRestoreAll={(next) => {
                setRestoreAll(next);
                if (next) setSelected({});
              }}
              onToggleCollection={handleToggleCollection}
            />

            <hr />

            <Form.Group>
              <Form.Label>
                Confirmation: Type <strong>RESTORE</strong> to confirm
              </Form.Label>
              <Form.Control
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder='Type "RESTORE"'
                disabled={isScheduling}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <AdminButton intent="secondary" onClick={handleClose} disabled={isScheduling}>
          Cancel
        </AdminButton>
        <AdminButton intent="danger" onClick={handleRestore} disabled={!canSubmit || isScheduling}>
          {isScheduling ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Scheduling...
            </>
          ) : (
            "Restore"
          )}
        </AdminButton>
      </Modal.Footer>
    </Modal>
  );
}

